import { useState } from 'react';
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Switch,
  Table,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSpaceContext } from '@/contexts/SpaceContext';
import {
  useApplyAutoJoinRules,
  useAutoJoinRules,
  useDeleteAutoJoinRule,
  useUpdateAutoJoinRule,
} from '@/hooks/useAutoJoinRules';
import { AutoJoinRuleModal } from './AutoJoinRuleModal';
import { summarizeCondition } from '@/lib/auto-join-conditions';
import type { AutoJoinTargetRole, SpaceAutoJoinRule } from '@/types/spaces';

const ROLE_BADGE: Record<AutoJoinTargetRole, 'info' | 'teal' | 'gray'> = {
  admin: 'info',
  member: 'teal',
  viewer: 'gray',
};

export function AutoJoinRulesTab() {
  const { spaceId, space } = useSpaceContext();
  const { success, error: showError } = useToast();

  const { data: rules = [], isLoading } = useAutoJoinRules(spaceId);
  const updateRule = useUpdateAutoJoinRule(spaceId);
  const deleteRule = useDeleteAutoJoinRule(spaceId);
  const applyRules = useApplyAutoJoinRules(spaceId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SpaceAutoJoinRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpaceAutoJoinRule | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (rule: SpaceAutoJoinRule) => {
    setEditing(rule);
    setModalOpen(true);
  };

  const handleToggle = async (rule: SpaceAutoJoinRule) => {
    try {
      await updateRule.mutateAsync({
        ruleId: rule.ruleId,
        input: { enabled: !rule.enabled },
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update rule');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRule.mutateAsync(deleteTarget.ruleId);
      success('Rule deleted');
      setDeleteTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete rule');
    }
  };

  const handleApply = async () => {
    try {
      await applyRules.mutateAsync();
      success('Applying rules across your org — memberships will update shortly.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to apply rules');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Rule',
      render: (r: SpaceAutoJoinRule) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground">{r.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {r.conditions.map(summarizeCondition).join('  AND  ')}
          </p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Grants',
      className: 'whitespace-nowrap',
      render: (r: SpaceAutoJoinRule) => (
        <Badge variant={ROLE_BADGE[r.targetRole]}>{r.targetRole}</Badge>
      ),
    },
    {
      key: 'enabled',
      header: 'Enabled',
      className: 'whitespace-nowrap',
      render: (r: SpaceAutoJoinRule) => (
        <Switch
          checked={r.enabled}
          onCheckedChange={() => handleToggle(r)}
          disabled={updateRule.isPending}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right whitespace-nowrap w-10',
      render: (r: SpaceAutoJoinRule) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Rule actions"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => openEdit(r)}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => setDeleteTarget(r)}
              className="text-red-600 focus:text-red-700"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Auto-join rules</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Automatically add people to{' '}
            <span className="font-medium text-foreground">{space?.name ?? 'this space'}</span>{' '}
            based on their directory attributes (department, manager, location). Rules
            re-evaluate when a user's profile changes; manually-added members are never
            affected.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            onClick={handleApply}
            isLoading={applyRules.isPending}
            disabled={rules.length === 0}
          >
            Apply now
          </Button>
          <Button onClick={openCreate}>New rule</Button>
        </div>
      </div>

      <Table
        columns={columns}
        data={rules}
        keyExtractor={(r) => r.ruleId}
        emptyMessage="No auto-join rules yet. Click 'New rule' to add one."
        isLoading={isLoading}
      />

      <AutoJoinRuleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        spaceId={spaceId}
        rule={editing}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete rule?"
        message={
          `Delete "${deleteTarget?.name ?? 'this rule'}"? Members who joined only because ` +
          `of this rule will be removed on the next reconcile. Manually-added members stay.`
        }
        confirmText="Delete"
        isLoading={deleteRule.isPending}
      />
    </div>
  );
}
